import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import { Trash2 } from 'lucide-react';
import * as React from 'react';

import type { IDefinitionSurvey } from '../../api/definitions-contracts';
import { internalCatalogueApi } from '../../api/internal-catalogue-api';
import type {
  IInternalFieldDetail,
  IWriteInternalFieldOption,
  InternalFieldAccess
} from '../../api/internal-catalogue-contracts';
import { Fact } from '../../components/admin-page';
import { refusalMessage } from '../../refusal';
import { ACCESS_OPTIONS, OptionRows, SELECT_NONE } from './choices';
import { ReasonClasses, isQualityRating } from './reason-classes';

/**
 * Every write on this card, with the one rule every admin write keeps: a refusal is
 * `refusalMessage`'s, a success re-reads, and the card is released either way. Four hand-rolled
 * copies of it is four places to forget one of the three.
 */
function useWrite(onChanged: () => void, onFailed: (message: string) => void) {
  const [busy, setBusy] = React.useState(false);
  const run = async (write: () => Promise<unknown>, fallback: string, done = onChanged) => {
    setBusy(true);
    try {
      await write();
      done();
    } catch (error) {
      onFailed(refusalMessage(error, fallback));
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

/**
 * One staff field, as the catalogue holds it (SR-226) — what it is, the values it offers, and
 * the surveys it is drawn on. Three independent writes, each its own whole-block `PUT`, because
 * that is how survey-api takes them: the field, the option membership, one binding.
 *
 * **What is immutable is said, not hidden.** `type` and `scope` cannot change after creation —
 * survey-api answers `400 immutable-member`, and a scope change would have to rewrite every
 * stored value through a composite key — so they are drawn as facts with the reason beside them,
 * never as a disabled control, which reads as "not yet".
 *
 * **Retire is the ordinary end of a field; delete is the rare one.** `DELETE` is refused `409
 * field-in-use` while any value holds the field, which for anything that has been filled in is
 * always. Both are offered, the refusal is said in survey-api's own words, and retiring is what
 * the copy points at.
 *
 * **An option is retired, never dropped, once a value holds it.** The membership `PUT` replaces
 * the list and refuses `400 option-in-use` for a code some value still names. A retired option
 * stays in the list, struck through, because it is what labels that stored value; removing the
 * row is offered for one nothing has used.
 *
 * **Origin is a fact about where the field came from, and gates nothing.** `migrated` is the
 * Salesforce import's, `authored` a person's here. Every write on this card is accepted for
 * both; what differs is that a later import may speak about a migrated field again, and the
 * card says so rather than taking the control away.
 */
export function StaffFieldDetail({
  field,
  surveys,
  onChanged,
  onRemoved,
  onFailed
}: {
  field: IInternalFieldDetail;
  surveys: IDefinitionSurvey[];
  onChanged: () => void;
  onRemoved: () => void;
  onFailed: (message: string) => void;
}) {
  // The two option cards start from what they read: a write by either is read again, and both
  // start over from it, so neither saves back what the other changed.
  const optionsKey = JSON.stringify(field.options);
  return (
    // Addressed by the field's code, the `data-member` idiom: a name is editable and two fields
    // may share one, where a code is the catalogue's primary key.
    <div data-field={field.code} className="grid gap-6">
      <Identity field={field} onChanged={onChanged} onRemoved={onRemoved} onFailed={onFailed} />
      {(field.type === 'select' || field.type === 'multi_select') && (
        <Options key={optionsKey} field={field} onChanged={onChanged} onFailed={onFailed} />
      )}
      {isQualityRating(field) && (
        <ReasonClasses key={optionsKey} field={field} onChanged={onChanged} onFailed={onFailed} />
      )}
      <Bindings field={field} surveys={surveys} onChanged={onChanged} onFailed={onFailed} />
    </div>
  );
}

function Identity({
  field,
  onChanged,
  onRemoved,
  onFailed
}: {
  field: IInternalFieldDetail;
  onChanged: () => void;
  onRemoved: () => void;
  onFailed: (message: string) => void;
}) {
  const [name, setName] = React.useState(field.name);
  const [access, setAccess] = React.useState<InternalFieldAccess>(field.access);
  const { busy, run } = useWrite(onChanged, onFailed);
  const changed = name !== field.name || access !== field.access;

  // `type` and `scope` go as the assertion survey-api reads them as: sending what is stored
  // asserts it is still that, and the write is refused if the catalogue moved underneath.
  const write = (block: { name: string; access: InternalFieldAccess; retired: boolean }) =>
    internalCatalogueApi.update(field.code, {
      ...block,
      type: field.type,
      scope: field.scope,
      constraint: field.constraint
    });

  const saveEdits = () =>
    run(
      () => write({ name: name.trim(), access, retired: field.retired }),
      'Could not save the field'
    );

  // Retiring sends the field **as stored**: the route takes the whole block, so it would
  // otherwise commit a half-typed name the author never saved.
  const setRetired = (retired: boolean) =>
    run(
      () => write({ name: field.name, access: field.access, retired }),
      retired ? 'Could not retire the field' : 'Could not bring the field back'
    );

  const remove = () =>
    run(() => internalCatalogueApi.remove(field.code), 'Could not remove the field', onRemoved);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {field.name}
          {field.retired && <Badge variant="outline">retired</Badge>}
          <Badge variant="soft">{field.origin}</Badge>
        </CardTitle>
        <CardDescription className="font-mono">{field.code}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {field.origin === 'migrated' && (
          <Alert>
            <AlertTitle>This field came from the Salesforce import</AlertTitle>
            <AlertDescription>
              Every change here is accepted. A later import speaks about this field again, so
              where the two disagree about a name or an option label, the import can have the
              last word — it leaves a retirement a person set alone, and nothing else.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="grid gap-1 text-sm">
            Name
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              aria-label="Name"
            />
          </label>
          <label className="grid gap-1 text-sm">
            Who may see it
            <Select
              value={access}
              onValueChange={(value) => setAccess(value as InternalFieldAccess)}
            >
              <SelectTrigger aria-label="Who may see it">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACCESS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <Fact
            label="Type"
            value={
              <>
                <code className="text-sm">{field.type}</code>
                <span className="mt-1 block text-xs text-muted-foreground">
                  Fixed at creation — a value is stored under it.
                </span>
              </>
            }
          />
          <Fact
            label="Scope"
            value={
              <>
                <code className="text-sm">{field.scope}</code>
                <span className="mt-1 block text-xs text-muted-foreground">
                  Fixed at creation — changing it would rewrite every stored value.
                </span>
              </>
            }
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button disabled={busy || !changed} onClick={() => void saveEdits()}>
            Save
          </Button>
          <Button variant="outline" disabled={busy} onClick={() => void setRetired(!field.retired)}>
            {field.retired ? 'Bring back' : 'Retire'}
          </Button>
          <Button variant="outline" disabled={busy} onClick={() => void remove()}>
            <Trash2 />
            Delete
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Retiring takes the field off every form and stops new values; the values it already
          holds stay, and keep their labels. Deleting is refused while any value holds it.
        </p>
      </CardContent>
    </Card>
  );
}

function Options({
  field,
  onChanged,
  onFailed
}: {
  field: IInternalFieldDetail;
  onChanged: () => void;
  onFailed: (message: string) => void;
}) {
  const [rows, setRows] = React.useState<IWriteInternalFieldOption[]>(() =>
    field.options.map((option) => ({
      code: option.code,
      label: option.label,
      retired: option.retired,
      meta: option.meta
    }))
  );
  const { busy, run } = useWrite(onChanged, onFailed);

  const save = () =>
    run(
      () =>
        internalCatalogueApi.setOptions(
          field.code,
          rows.map((row) => ({ ...row, code: row.code.trim(), label: row.label.trim() }))
        ),
      'Could not save the values'
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Values</CardTitle>
        <CardDescription>
          The whole list is saved at once, and the order here is the order a reader sees. A value
          some record already holds cannot be removed — retire it instead, and it keeps labelling
          what it labelled.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <OptionRows rows={rows} retirable onChange={setRows} />
        <div className="flex">
          <Button className="ml-auto" disabled={busy} onClick={() => void save()}>
            Save values
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Bindings({
  field,
  surveys,
  onChanged,
  onFailed
}: {
  field: IInternalFieldDetail;
  surveys: IDefinitionSurvey[];
  onChanged: () => void;
  onFailed: (message: string) => void;
}) {
  const [adding, setAdding] = React.useState(SELECT_NONE);
  const { busy, run } = useWrite(onChanged, onFailed);
  const bound = new Set(field.bindings.map((binding) => binding.surveyCode));
  const free = surveys.filter((survey) => !bound.has(survey.code));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Where it appears</CardTitle>
        <CardDescription>
          A field is global; each survey it is bound to draws it. The section it sits in and its
          place in that section are set under Forms.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        {field.bindings.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Bound to no survey — it exists in the catalogue and is drawn nowhere.
          </p>
        )}
        {field.bindings.map((binding) => (
          <div
            key={binding.surveyCode}
            className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3 text-sm"
          >
            <span className="font-medium">
              {surveys.find((survey) => survey.code === binding.surveyCode)?.name ??
                binding.surveyCode}
            </span>
            <code className="text-xs text-muted-foreground">{binding.surveyCode}</code>
            <span className="text-xs text-muted-foreground">
              {binding.groupCode === null ? 'on no section' : binding.groupCode} · position{' '}
              {binding.ordinal}
            </span>
            <Button
              className="ml-auto"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() =>
                void run(
                  () => internalCatalogueApi.unbind(field.code, binding.surveyCode),
                  'Could not take the field off that survey'
                )
              }
            >
              Remove
            </Button>
          </div>
        ))}
        {free.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Select value={adding} onValueChange={setAdding}>
              <SelectTrigger className="w-64" aria-label="Add to a survey">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SELECT_NONE}>Add to a survey…</SelectItem>
                {free.map((survey) => (
                  <SelectItem key={survey.code} value={survey.code}>
                    {survey.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              disabled={busy || adding === SELECT_NONE}
              onClick={() =>
                void run(async () => {
                  await internalCatalogueApi.bind(field.code, adding);
                  setAdding(SELECT_NONE);
                }, 'Could not add the field to that survey')
              }
            >
              Add
            </Button>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Taking a field off a survey is refused while a record of that survey holds a value for
          it.
        </p>
      </CardContent>
    </Card>
  );
}
